import React, {
	useEffect,
	useState,
	useCallback,
	useMemo,
	type SyntheticEvent,
	type MouseEvent,
	type KeyboardEvent,
	type ComponentType,
} from 'react';
import { styled } from '@compiled/react';
import FormIcon from '@atlaskit/icon/core/form';
import GlobeIcon from '@atlaskit/icon/core/globe';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import ArchiveIcon from '@atlaskit/icon/core/migration/archive-box--archive';
import IconBacklogDI from '@atlaskit/icon/glyph/backlog';
import IconBoardDI from '@atlaskit/icon/glyph/board';
import IconListDI from '@atlaskit/icon/glyph/bullet-list';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import IconCodeDI from '@atlaskit/icon/glyph/code';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import IconChartDI from '@atlaskit/icon/glyph/graph-line';
import IconIssuesDI from '@atlaskit/icon/glyph/issues';
import LockIcon from '@atlaskit/icon/glyph/lock';
import IconRoadmapDI from '@atlaskit/icon/glyph/roadmap';
import IconSettingsDI from '@atlaskit/icon/glyph/settings';
import IconShipDI from '@atlaskit/icon/glyph/ship';
import IconUploadDI from '@atlaskit/icon/glyph/upload';
import Lozenge from '@atlaskit/lozenge';
import {
	NavigationFooter as NavigationFooterDI,
	NavigationHeader as NavigationHeaderDI,
	NestableNavigationContent as NestableNavigationContentDI,
	Section as SectionDI,
	SideNavigation as SideNavigationDI,
	SkeletonItem as SkeletonItemDI,
} from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { useRouter as useRouterDI, type JiraSpaRoute } from '@atlassian/react-resource-router';
import { isFedRamp } from '@atlassian/atl-context';
import {
	SOFTWARE_PROJECT,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { CONNECT_ROUTES_SET } from '@atlassian/jira-common-constants/src/spa-routes-sets.tsx';
import {
	determineProductCrossSellConfig,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { AsyncMultiProductUpgradeModal } from '@atlassian/jira-experiments/src/ui/experiments/multi-product-upgrade/async.tsx';
import { useMultiProductUpgrade } from '@atlassian/jira-experiments/src/ui/experiments/multi-product-upgrade/index.tsx';
import { expValEquals, expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { Shortcuts as ShortcutsDI } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/ui/main.tsx';
import { EngagementSpotlight as EngagementSpotlightDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/engagement-spotlight/main.tsx';
import { ExpandableSection as ExpandableSectionDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-section/index.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import {
	NavigationHeaderWrapper as NavigationHeaderWrapperDI,
	NavigationExpandableContentWrapper as NavigationExpandableContentWrapperDI,
	NavigationContentWrapper as NavigationContentWrapperDI,
	ShortCutsAndAppsWrapper as ShortCutsAndAppsWrapperDI,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/navigation-component-wrappers/index.tsx';
import { ScreenReaderMenuHeader as ScreenReaderMenuHeaderDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/screen-reader-header/index.tsx';
import { SpotlightTarget as SpotlightTargetDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import { useBoard as useBoardDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useJiraSoftwareProjectPagesLink } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project-pages-link/main.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useIncidentExperimentTraits as useIncidentExperimentTraitsDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/software-incidents/index.tsx';
import {
	setNavModulesCollapseState,
	useNavModulesCollapseState,
	type NavModulesCollapsedType,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/services/nav-modules/index.tsx';
import { RegisterProjectNavigationCommands } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/command-palette/index.tsx';
import { ContextualAnalyticsData as ContextualAnalyticsDataDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { GoalIcon } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/goal-icon/index.tsx';
import { Issues as IssuesDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/issues/main.tsx';
import { NestingMenuItem as NestingMenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import type { NestingMenuItemProps } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/types.tsx';
import { ProjectHeader as ProjectHeaderDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/project-header/main.tsx';
import type { ProjectHeaderProps } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/project-header/types.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlPathname as matchUrlPathnameDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { ConnectMenu as ConnectMenuDI } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/ui/connect-menu/main.tsx';
import { useProjectConnectItems as useProjectConnectItemsDI } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project/index.tsx';
import { useProjectSettingsForgeItems } from '@atlassian/jira-navigation-apps-sidebar-core/src/controllers/index.tsx';
import { useProjectForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project/index.tsx';
import { ForgeProjectSection as ForgeProjectSectionDI } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/project-section/index.tsx';
import { useIsEditableToInline } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/controllers/is-editable-to-inline/index.tsx';
import { InlineConfigurableProjectHeaderAsync } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/ui/async.tsx';
import { useFeatures as useFeaturesDI } from '@atlassian/jira-navigation-apps-sidebar-next-gen-use-features/src/controllers/features/index.tsx';
import { CustomTemplateInlineMessage } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/custom-template-inline-message/main.tsx';
import {
	getReturnToProjectUrl,
	setReturnToProjectUrl,
} from '@atlassian/jira-navigation-apps-sidebar-project-utils/src/index.tsx';
import { useIsSimplifyNavExperimentEnabled } from '@atlassian/jira-onboarding-simplify-navigation-experiment/src/common/utils/is-simplify-nav-experiment-enabled/index.tsx';
import { OnCallIcon as IconOpsGenieDI } from '@atlassian/jira-opsgenie-schedule/src/common/ui/index.tsx';
import { useRouteByName } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import {
	FireUiAnalytics as FireUiAnalyticsDI,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/import/file-extension-required -- Required to exclude from SSR
import { JswSideNavRecommendation } from '@atlassian/jira-side-nav-recommendation';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import isIncidentsPageEnabled from '@atlassian/jira-software-incidents-experiment/src/index.tsx';
import { useApplicationPermissions as useApplicationPermissionsDI } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useLicensedProducts as useLicensedProductsDI } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { AddFeature } from '@atlassian/jira-tmp-feature-toggle/src/ui/index.tsx';
import { LazySuspense } from '@atlassian/react-loosely-lazy';

import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import { MENU_ID, SECTION_ID } from '../../common/constants/index.tsx';
import { Footer as FooterDI } from './footer/main.tsx';
import { messages } from './messages.tsx';
import { Reports as ReportsDI } from './reports/main.tsx';
import { SettingsMenu as SettingsMenuDI } from './settings/main.tsx';
import type { MenuProps } from './types.tsx';
import { WithFeatureToggle } from './with-feature-toggle/index.tsx';

const isConnectOpen = (route: JiraSpaRoute) => CONNECT_ROUTES_SET.has(route.name);

const isForgeOpen = (route: JiraSpaRoute) =>
	[
		'forge-spa-project',
		'forge-spa-project-servicedesk',
		'project-settings-software-apps-forge',
	].includes(route.name);

const calculateStack = (
	route: JiraSpaRoute,
	connectStack: null | Array<string>,
	forgeStack: null | Array<string>,
) => {
	const routeName: RouteName = route && route.name;
	if (['issue-navigator'].includes(route && route.name)) {
		return [SECTION_ID.ISSUES];
	}

	if (
		[
			'software-reports',
			'software-reports-burndown',
			'software-reports-burnup',
			'software-reports-cumulative',
			'software-reports-velocity',
			'software-reports-cycle-time',
			'software-reports-deployment-frequency',
			'software-reports-control',
		].includes(routeName)
	) {
		return [SECTION_ID.REPORTS];
	}

	if (isConnectOpen(route) && connectStack) {
		return connectStack;
	}

	if (isForgeOpen(route) && forgeStack) {
		return forgeStack;
	}

	if (routeName.includes('project-settings-software')) {
		const [view] = routeName.replace(`${'project-settings-software'}-`, '').split('-');
		switch (view) {
			case 'issuetypes':
				return [SECTION_ID.SETTINGS, view];
			case 'apps':
				return [SECTION_ID.SETTINGS];
			default:
				return [SECTION_ID.SETTINGS];
		}
	}

	return [];
};

const calculateProjectSettingsOpen = ({ name }: JiraSpaRoute) =>
	name.includes('project-settings-software');

// @ts-expect-error - TS7006 - Parameter 'features' implicitly has an 'any' type.
const hasMultipleNavModulesActive = (features) =>
	!!features &&
	(features.hasCode || features.hasReleases || features.hasDeployments || features.hasOpsgenie);

export const useFeatures = withThrowOnError(useFeaturesDI);
export const useBoard = withThrowOnError(useBoardDI);
export const useProject = withThrowOnError(useProjectDI);
export const useProjectConnectItems = withThrowOnError(useProjectConnectItemsDI);

export const useThrowJiraSoftwareProjectPagesLink = withThrowOnError(
	useJiraSoftwareProjectPagesLink,
);

const FooterSimplifyNavExperimentInner = () => {
	const isTargetedForSimplifiyNavigationExperiment = useIsSimplifyNavExperimentEnabled();

	return isTargetedForSimplifiyNavigationExperiment ? null : <FooterDI />;
};

const FooterSimplifyNavExperiment = () => (
	<JSErrorBoundary
		packageName="jiraNavigationAppsSidebarNextGen"
		id={`${MENU_ID}.footer`}
		teamName="jlove-makkuro"
		fallback={() => <FooterDI />}
	>
		<FooterSimplifyNavExperimentInner />
	</JSErrorBoundary>
);

const ConfigurableProjectHeader = ({
	name,
	avatarUrl,
	projectId,
	description,
	ProjectHeader,
}: {
	name: string;
	avatarUrl: string;
	projectId: number;
	description: string;
	ProjectHeader: ComponentType<ProjectHeaderProps>;
}) => {
	const shouldShowInlineConfigProjectHeader = useIsEditableToInline();
	return shouldShowInlineConfigProjectHeader() ? (
		<InlineConfigurableProjectHeaderAsync
			key={projectId}
			avatarUrl={avatarUrl}
			description={description}
			name={name}
			projectId={projectId}
			canEdit
		/>
	) : (
		<ProjectHeader avatarUrl={avatarUrl} description={description} name={name} />
	);
};

export const Menu = ({
	ConnectMenu = ConnectMenuDI,
	ContextualAnalyticsData = ContextualAnalyticsDataDI,
	EngagementSpotlight = EngagementSpotlightDI,
	ExpandableSection = ExpandableSectionDI,
	Footer = FooterDI,
	FireUiAnalytics = FireUiAnalyticsDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconBacklog = IconBacklogDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconIncidents = ErrorIcon,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconSecurity = LockIcon,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconBoard = IconBoardDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconChart = IconChartDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconCode = IconCodeDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconIssues = IconIssuesDI,
	IconOpsGenie = IconOpsGenieDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconRoadmap = IconRoadmapDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconList = IconListDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconSettings = IconSettingsDI,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconShip = IconShipDI,
	// @ts-expect-error - TS2339 - Property 'IconUpload' does not exist on type 'MenuProps'.
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	IconUpload = IconUploadDI,
	Issues = IssuesDI,
	MenuItem = MenuItemDI,
	NavigationFooter = NavigationFooterDI,
	NavigationHeader = NavigationHeaderDI,
	NestableNavigationContent = NestableNavigationContentDI,
	NestingMenuItem = NestingMenuItemDI,
	NavigationContentWrapper = NavigationContentWrapperDI,
	NavigationExpandableContentWrapper = NavigationExpandableContentWrapperDI,
	NavigationHeaderWrapper = NavigationHeaderWrapperDI,
	ProjectHeader = ProjectHeaderDI,
	ForgeProjectSection = ForgeProjectSectionDI,
	Reports = ReportsDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	SettingsMenu = SettingsMenuDI,
	Shortcuts = ShortcutsDI,
	ShortCutsAndAppsWrapper = ShortCutsAndAppsWrapperDI,
	SideNavigation = SideNavigationDI,
	SpotlightTarget = SpotlightTargetDI,
	matchUrlPathname = matchUrlPathnameDI,
	useApplicationPermissions = useApplicationPermissionsDI,
	useIntl = useIntlDI,
	useLicensedProducts = useLicensedProductsDI,
	useRouter = useRouterDI,
	useIncidentExperimentTraits = useIncidentExperimentTraitsDI,
}: MenuProps) => {
	const [forgeSettingsStack] = useState([SECTION_ID.SETTINGS]);

	const { formatMessage } = useIntl();
	const [{ route, match }] = useRouter();
	const { data: features } = useFeatures();
	const { loading: boardLoading, data: board } = useBoard();
	const { data: project } = useProject();
	const { data: connectItems, stack: connectStack } = useProjectConnectItems();
	const { data: projectPagesData } = useThrowJiraSoftwareProjectPagesLink();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { stack: forgeProjectStack } = useProjectForgeApps('forge-spa-project');
	const { stack: forgeProjectServidedeskStack } = useProjectForgeApps(
		'forge-spa-project-servicedesk',
	);
	const { stack: forgeProjectSettingsStack } = useProjectSettingsForgeItems(
		'project-settings-software-apps-forge',
		forgeSettingsStack,
	);
	const navModulesCollapsedState = useNavModulesCollapseState();
	const licensedProducts = useLicensedProducts();

	const { hasSoftwareAccess } = useApplicationPermissions();
	const forgeStack = forgeProjectStack || forgeProjectServidedeskStack || forgeProjectSettingsStack;
	const [stack, setStack] = useState<string[]>(calculateStack(route, connectStack, forgeStack));

	const [navModuleCollapseState, setNavModuleCollapseState] = useState<NavModulesCollapsedType>(
		navModulesCollapsedState.nextGen,
	);

	// We need to check we're dealing with a nested menu for a connect item - if we navigate while inside a
	// nested connect item menu or if we're deep linking into a nested menu for a connect item -> we need to maintain the hiding of nav-modules
	const nestedEcosystemAppOpen = useMemo(
		() =>
			(isConnectOpen(route) && !!connectStack && connectStack.length > 0) ||
			(isForgeOpen(route) && !!forgeStack && forgeStack.length > 0),
		[connectStack, forgeStack, route],
	);

	const [nestedMenuActive, setNestedMenuOpen] = useState<boolean>(
		stack.includes(SECTION_ID.ISSUES) ||
			stack.includes(SECTION_ID.REPORTS) ||
			calculateProjectSettingsOpen(route) ||
			nestedEcosystemAppOpen,
	);

	const [hideExpandableSections, setHideExpandableSections] = useState<boolean>(
		// If the expandable project settings FF is not enabled, we need to remove expandable sections when
		// users navigate into nested project settings
		calculateProjectSettingsOpen(route) || nestedEcosystemAppOpen,
	);

	const [multipleNavModulesActive, setMultipleNavModulesActive] = useState<boolean>(
		hasMultipleNavModulesActive(features),
	);

	const reportsNestedItemOnClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			// Ignore cmd or ctrl clicks - neither of these should set nested menus open
			if (!event.metaKey && !event.ctrlKey) {
				setTimeout(() => setNestedMenuOpen(true));
			}
		},
		[setNestedMenuOpen],
	);

	const setNavModulesCollapseStateLocalStorage = useCallback(
		(key: string) => {
			const currentState = navModulesCollapsedState;

			const rtnObj = {
				...currentState.nextGen,
			};

			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ development: { isCollapsed: boolean; }; operations: { isCollapsed: boolean; }; planning: { isCollapsed: boolean; }; }'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'NavModulesCollapsedType'.
			rtnObj[key].isCollapsed = !currentState.nextGen[key].isCollapsed;

			setNavModulesCollapseState({
				...currentState,
				nextGen: {
					...rtnObj,
				},
			});

			// Update local state
			setNavModuleCollapseState(rtnObj);
		},
		[navModulesCollapsedState],
	);
	const setNavModulesCollapseStateLocalStoragePlanning = useCallback(
		() => setNavModulesCollapseStateLocalStorage('planning'),
		[setNavModulesCollapseStateLocalStorage],
	);
	const setNavModulesCollapseStateLocalStorageDevelopment = useCallback(
		() => setNavModulesCollapseStateLocalStorage('development'),
		[setNavModulesCollapseStateLocalStorage],
	);
	const setNavModulesCollapseStateLocalStorageOperations = useCallback(
		() => setNavModulesCollapseStateLocalStorage('operations'),
		[setNavModulesCollapseStateLocalStorage],
	);

	const nonExpandableSectionNestedOnClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			// Ignore cmd or ctrl clicks - neither of these should set nested menus open
			if (!event.metaKey && !event.ctrlKey) {
				setReturnToProjectUrl();
				setHideExpandableSections(true);
				// Wait till event loop finishes before setting nested menu open
				// without this, we get a little bit of jank transitioning back to the
				// main sidebar.
				// *issue persisted when using requestAnimationFrame in lieu of setTimeout
				setTimeout(() => setNestedMenuOpen(true));
			}
		},
		[setNestedMenuOpen, setHideExpandableSections],
	);

	const multiProductUpgradeProps = useMultiProductUpgrade();

	const { data: incidentExperimentTraits } = useIncidentExperimentTraits();

	useEffect(() => {
		const newStack = calculateStack(route, connectStack, forgeStack);
		setHideExpandableSections(calculateProjectSettingsOpen(route) || nestedEcosystemAppOpen);
		setStack(newStack);
		setNestedMenuOpen(
			newStack.includes(SECTION_ID.ISSUES) ||
				newStack.includes(SECTION_ID.REPORTS) ||
				calculateProjectSettingsOpen(route) ||
				nestedEcosystemAppOpen,
		);
	}, [route, match, connectStack, nestedEcosystemAppOpen, forgeStack]);

	useEffect(() => {
		setMultipleNavModulesActive(hasMultipleNavModulesActive(features));
	}, [features]);

	// Reports and issues were disappearing when deep linking when the planning section was collapsed
	// this state controls if the reports or issues items are open, so we can expand the Planning nav module temporarily
	const calculatePlanningOpen = useCallback(
		() => stack.includes('reports') || stack.includes('issues'),
		[stack],
	);

	const getBackMenuItem = useCallback(
		(preventNavigation = false): NestingMenuItemProps['overrides']['BackMenuItem'] => ({
			href:
				getReturnToProjectUrl() ||
				(project &&
					(board == null
						? `/browse/${project?.key}`
						: `/jira/software/projects/${project.key}/boards/${board.id}`)) ||
				'',
			title: formatMessage(messages.itemBack),
			onClick: (event: SyntheticEvent) => {
				if (preventNavigation) {
					event.preventDefault();
				}
				// Wait till event loop finishes before setting nested menu open
				// without this, we get a little bit of jank transitioning back to the
				// main sidebar.
				// *issue persisted when using requestAnimationFrame in lieu of setTimeout
				setTimeout(() => {
					setNestedMenuOpen(false);
					setHideExpandableSections(false);
				});
			},
		}),
		[project, board, formatMessage],
	);

	const deploymentsRoute = useRouteByName('software-deployments');
	const releasesRoute = useRouteByName('project-versions');
	const incidentsRoute = useRouteByName('software-incidents');

	if (
		features == null ||
		(board == null && boardLoading) ||
		project == null ||
		connectItems == null
	)
		return <SkeletonItem />;

	const projectUrl = `/jira/software/projects/${project.key}`;
	const timelineHref = `${projectUrl}/boards/${board?.id}/timeline`;
	const listHref = `${projectUrl}/list`;
	const backlogHref = `${projectUrl}/boards/${board?.id}/backlog`;
	const boardHref = `${projectUrl}/boards/${board?.id}`;
	const summaryHref = `${projectUrl}/summary`;
	const reportsHref = `${projectUrl}${`/boards/${board?.id}/reports`}`;
	const calendarHref = `${projectUrl}/boards/${board?.id}/calendar`;

	const settings = (
		<NestingMenuItem
			analytics={{ itemId: 'settings' }}
			href={`${projectUrl}/settings`}
			iconBefore={<IconSettings label="" />}
			id={SECTION_ID.SETTINGS}
			overrides={{
				BackMenuItem: getBackMenuItem(),
			}}
			title={formatMessage(messages.itemSettings)}
			selectedOn={matchUrlPathname()}
			onClick={nonExpandableSectionNestedOnClick}
		>
			<SettingsMenu stack={stack} />
		</NestingMenuItem>
	);

	const showOperationsSection =
		!isDevopsFeatureDisabledInFedRamp() &&
		(features.hasDeployments ||
			(features.hasOpsgenie &&
				(determineProductCrossSellConfig() === 'enabled' ||
					licensedProducts[LICENSED_PRODUCTS.OPSGENIE])) ||
			(features.hasIncidents &&
				((incidentExperimentTraits && isIncidentsPageEnabled(incidentExperimentTraits)) ||
					fg('corex_operandi_incidents_page_in_jira'))));

	const showDevelopmentSection =
		!isFedRamp() &&
		(features.hasCode || features.hasSecurity || features.hasDevelopment || features.hasReleases);
	const showCodeNavigationLink = !isDevopsFeatureDisabledInFedRamp() && features.hasCode;
	const showSecurityNavigationLink = !isDevopsFeatureDisabledInFedRamp() && features.hasSecurity;
	const showDevelopmentNavigationLink = fg('guava_development_page_in_jira_enabled')
		? !isFedRamp() && features.hasDevelopment && fg('jira-periscope-new-page')
		: !isFedRamp() && fg('jira-periscope-new-page');

	const projectType = project && toProjectType(project.type);

	const showMultiProductUpgradeModal =
		projectType &&
		projectType === SOFTWARE_PROJECT &&
		!multiProductUpgradeProps.isLoading &&
		multiProductUpgradeProps.isEnrolled;

	const shouldRenderFormItem = () =>
		expValEquals('forms_for_jsw_experiment', 'isEnabled', true) && features.hasForms;

	if (fg('issue_terminology_refresh_change_boarding_modal')) {
		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'projectOrIssuePage',
			action: 'viewed',
		});
		fireUIAnalytics(analyticsEvent, 'projectOrIssueScreen');
	}

	return (
		<ContextualAnalyticsData menuId={MENU_ID} stack={stack}>
			<SideNavigation
				label={formatMessage(messages.sidebarLabel)}
				isServer={__SERVER__}
				isSSRPlaceholderEnabled={
					// eslint-disable-next-line jira/ff/no-preconditioning
					(fg('add_ssr_placeholder_replacements_to_nin_and_nav') &&
						route.name === 'software-issue-navigator') ||
					(route.name === 'issue' && fg('add_ssr_placeholder_replacements_to_nav_for_iv'))
				}
			>
				<NavigationHeaderWrapper>
					<NavigationHeader>
						<>
							{fg('jsw_inline_config_project_fg') ? (
								<ConfigurableProjectHeader
									avatarUrl={project.avatarUrl}
									description={formatMessage(messages.headerDescriptionTeamManaged)}
									name={project.name}
									projectId={project.id}
									ProjectHeader={ProjectHeader}
								/>
							) : (
								<ProjectHeader
									avatarUrl={project.avatarUrl}
									description={formatMessage(messages.headerDescriptionTeamManaged)}
									name={project.name}
								/>
							)}
							{fg('jira_custom_templates_ux') && (
								<CustomTemplateInlineMessage isLiveTemplate={project.isLiveTemplate} />
							)}
						</>
					</NavigationHeader>
				</NavigationHeaderWrapper>
				<>
					{showMultiProductUpgradeModal && (
						<LazySuspense fallback={null}>
							<AsyncMultiProductUpgradeModal {...multiProductUpgradeProps} />
						</LazySuspense>
					)}
				</>
				<NestableNavigationContent stack={stack} onChange={setStack} isDefaultFocusControl={false}>
					<NavigationExpandableContentWrapper
						nestedAppOpen={hideExpandableSections}
						submenuActive={nestedMenuActive}
					>
						<ScreenReaderMenuHeaderDI />
						<ExpandableSection
							context="nextGen"
							title={multipleNavModulesActive ? formatMessage(messages.planningModule) : undefined}
							submenuActive={nestedMenuActive}
							isCollapsed={navModuleCollapseState.planning.isCollapsed && !calculatePlanningOpen()}
							expandClick={setNavModulesCollapseStateLocalStoragePlanning}
							removeTitleSection={!multipleNavModulesActive}
						>
							{/* features.hasSummaryPage is a backend value which includes the evaluation of */}
							{/* `helios_summary_page_experiment` without exposure so we don't want to exclude the users which */}
							{/* evaluates to false from the experiment which is why we place the check at the end */}
							{/* eslint-disable-next-line jira/ff/no-preconditioning */}
							{projectType === SOFTWARE_PROJECT &&
								expVal('helios_summary_page_experiment', 'isEnabled', false) &&
								features.hasSummaryPage && (
									<MenuItem
										analytics={{ itemId: 'summary' }}
										data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-summary-link"
										to={summaryHref}
										prefetch="hover"
										iconBefore={<GlobeIcon label="" color="currentColor" />}
										selectedOn={['software-summary']}
									>
										{formatMessage(messages.itemSummary)}
									</MenuItem>
								)}
							{board != null && (
								<WithFeatureToggle
									feature="jsw.agility.simple-roadmap"
									hasFeature={features.hasRoadmap && board != null}
								>
									<MenuItem
										analytics={{ itemId: 'roadmap' }}
										iconBefore={<IconRoadmap label="" />}
										href={timelineHref}
										selectedOn={['software-timeline']}
										data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-roadmap-link"
									>
										{formatMessage(messages.itemTimeline)}
									</MenuItem>
								</WithFeatureToggle>
							)}
							{board != null && (
								<WithFeatureToggle
									feature="jsw.agility.backlog"
									hasFeature={features.hasBacklog && board != null}
								>
									<MenuItem
										analytics={{ itemId: 'backlog' }}
										to={backlogHref}
										prefetch="hover"
										data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-backlog-link"
										iconBefore={<IconBacklog label="" />}
										selectedOn={['software-backlog']}
									>
										{formatMessage(messages.itemBacklog)}
									</MenuItem>
								</WithFeatureToggle>
							)}
							{features.hasBoard && board != null && (
								<>
									<SpotlightTarget name="spotlight-sidebar-next-gen-board">
										<MenuItem
											analytics={{ itemId: 'board' }}
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-board-link"
											to={boardHref}
											prefetch="hover"
											iconBefore={<IconBoard label="" />}
											selectedOn={['software-boards']}
										>
											{formatMessage(messages.itemBoard)}
										</MenuItem>
									</SpotlightTarget>
									<EngagementSpotlight engagementId="spotlight-sidebar-next-gen-board" />
								</>
							)}

							{board && features.hasCalendar && (
								<MenuItem
									analytics={{
										itemId: 'calendar',
									}}
									selectedOn={['software-calendar']}
									iconBefore={<CalendarIcon spacing="spacious" label="" />}
									to={calendarHref}
									prefetch="hover"
									testId="navigation-apps-sidebar-next-gen.ui.menu.calendar-link"
								>
									{formatMessage(messages.itemCalendar)}
								</MenuItem>
							)}

							{board != null && expValEquals('jsw_list_view', 'cohort', 'variation') && (
								<WithFeatureToggle feature="jsw.agility.list_view" hasFeature={features.hasList}>
									<MenuItem
										analytics={{ itemId: 'list' }}
										iconBefore={<IconList label="" />}
										href={listHref}
										selectedOn={['software-list']}
										data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-list-link"
									>
										{formatMessage(messages.itemList)}
									</MenuItem>
								</WithFeatureToggle>
							)}
							{shouldRenderNesting(
								features.hasReports && board != null,
								stack,
								SECTION_ID.REPORTS,
							) &&
								board != null && (
									<NestingMenuItem
										analytics={{ itemId: 'reports' }}
										onClick={reportsNestedItemOnClick}
										href={reportsHref}
										iconBefore={<IconChart label="" />}
										id={SECTION_ID.REPORTS}
										overrides={{
											BackMenuItem: getBackMenuItem(),
										}}
										selectedOn={['software-reports']}
										title={formatMessage(messages.itemReports)}
										data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-reports-link"
									>
										<Reports />
									</NestingMenuItem>
								)}
							{shouldRenderFormItem() && (
								<MenuItem
									analytics={{ itemId: 'forms' }}
									iconBefore={<FormIcon label="" color="currentColor" />}
									href={`/jira/software/projects/${project.key}/form`}
									selectedOn={['software-form', 'software-form-builder']}
									data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-forms-link"
								>
									{formatMessage(messages.itemForms)}
								</MenuItem>
							)}
							{features.hasGoals && (
								<MenuItem
									analytics={{ itemId: 'goals' }}
									iconBefore={<GoalIcon />}
									href={`${projectUrl}/goals`}
									selectedOn={['software-goals']}
									data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-goals-link"
								>
									{formatMessage(messages.itemGoals)}
								</MenuItem>
							)}
							<WithFeatureToggle feature="jsw.agility.issue_navigator" hasFeature={features.hasSIN}>
								<MenuItem
									analytics={{ itemId: 'simple-issue-navigator' }}
									href={`${projectUrl}/issues/`}
									iconBefore={<IconIssues label="" />}
									selectedOn={['software-issue-navigator', 'issue']}
									data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-simple-issues-link"
								>
									<>
										{formatMessage(
											expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
												? messages.itemSinIssueTermRefresh
												: messages.itemSin,
										)}
									</>
								</MenuItem>
							</WithFeatureToggle>
							{shouldRenderNesting(hasSoftwareAccess !== true, stack, SECTION_ID.ISSUES) && (
								<NestingMenuItem
									analytics={{ itemId: 'project-issue-navigator' }}
									onClick={reportsNestedItemOnClick}
									href={`/projects/${project.key}/issues`}
									iconBefore={<IconIssues label="" />}
									id={SECTION_ID.ISSUES}
									overrides={{
										BackMenuItem: getBackMenuItem(),
									}}
									selectedOn={['issue-navigator', 'issue']}
									title={formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.itemPinIssueTermRefresh
											: messages.itemPin,
									)}
									data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-issues-link"
								>
									<Issues projectKey={project.key} />
								</NestingMenuItem>
							)}
						</ExpandableSection>
						<AddFeature
							featureStatusList={features}
							projectUrl={projectUrl}
							boardId={board?.id}
							onNavigateToFeaturesSetting={nonExpandableSectionNestedOnClick}
						/>
						{showDevelopmentSection && (
							<ExpandableSection
								title={formatMessage(messages.developmentModule)}
								submenuActive={nestedMenuActive}
								isCollapsed={navModuleCollapseState.development.isCollapsed}
								expandClick={setNavModulesCollapseStateLocalStorageDevelopment}
							>
								{showDevelopmentNavigationLink && (
									<JSErrorBoundary
										id="periscopeMenuItem"
										teamName="nova"
										packageName="jiraNavigationAppsSidebarNextGen"
									>
										<MenuItem
											analytics={{ itemId: 'periscope' }}
											selectedOn={['software-periscope']}
											iconBefore={<LightbulbIcon label="" color="currentColor" />}
											href={`/jira/software/projects/${project.key}/development`}
											testId="navigation-apps-sidebar-next-gen.ui.menu.work-suggestions-link"
										>
											{formatMessage(messages.itemPeriscopeNonFinal)}
											<LozengeContainer>
												<Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>
											</LozengeContainer>
										</MenuItem>
									</JSErrorBoundary>
								)}
								{showCodeNavigationLink && (
									<>
										<SpotlightTarget name="spotlight-sidebar-code-next-gen">
											<MenuItem
												analytics={{
													itemId: 'code',
													// @ts-expect-error TS2322 | TS2322 - Type '{ itemId: string; nextgen: true; projectId: any; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'. | Type '{ itemId: string; nextgen: true; projectId: any; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'.
													nextgen: true,
													projectId: project.id,
												}}
												href={`${projectUrl}/code`}
												iconBefore={<IconCode label="" />}
												selectedOn={['software-code']}
												data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-code-link"
											>
												{formatMessage(messages.itemCode)}
											</MenuItem>
											<FireUiAnalytics
												actionSubject="codeNavigationItem"
												attributes={{
													nextgen: true,
												}}
												action="viewed"
											/>
										</SpotlightTarget>
										<EngagementSpotlight engagementId="spotlight-sidebar-code-next-gen" />
									</>
								)}
								{showSecurityNavigationLink && fg('isotopes-security-page-in-jira-enabled-v2') && (
									<MenuItem
										selectedOn={['software-security']}
										iconBefore={<IconSecurity label="" />}
										href={`${projectUrl}/security`}
										testId="navigation-apps-sidebar-next-gen.ui.menu.security-link"
									>
										{formatMessage(messages.itemSecurity)}
									</MenuItem>
								)}
								{features.hasReleases &&
									(releasesRoute !== null ? (
										<MenuItem
											analytics={{ itemId: 'releases' }}
											to={releasesRoute}
											params={{ projectKey: project.key }}
											prefetch="hover"
											query={{
												selectedItem: 'com.atlassian.jira.jira-projects-plugin:release-page',
											}}
											iconBefore={<IconShip label="" />}
											selectedOn={[
												'project-versions',
												'legacy-project-releases',
												'legacy-project-releases-bamboo',
												'legacy-project-releases-note',
												'legacy-project-releases-note-config',
												'software-release-notes',
												'software-version-detail',
											]}
											// Note: this test ID is used in a Pollinator check
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-releases-link"
										>
											{formatMessage(messages.itemReleases)}
										</MenuItem>
									) : (
										<MenuItem
											analytics={{ itemId: 'releases' }}
											href={`/projects/${project.key}?selectedItem=com.atlassian.jira.jira-projects-plugin:release-page`}
											iconBefore={<IconShip label="" />}
											selectedOn={[
												'project-versions',
												'legacy-project-releases',
												'legacy-project-releases-bamboo',
												'legacy-project-releases-note',
												'legacy-project-releases-note-config',
												'software-release-notes',
												'software-version-detail',
											]}
											// Note: this test ID is used in a Pollinator check
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-releases-link"
										>
											{formatMessage(messages.itemReleases)}
										</MenuItem>
									))}
							</ExpandableSection>
						)}
						{showOperationsSection && (
							<ExpandableSection
								title={formatMessage(messages.operationsModule)}
								submenuActive={nestedMenuActive}
								isCollapsed={navModuleCollapseState.operations.isCollapsed}
								expandClick={setNavModulesCollapseStateLocalStorageOperations}
							>
								{features.hasDeployments &&
									(deploymentsRoute !== null ? (
										<MenuItem
											analytics={{
												itemId: 'deployments',
											}}
											to={deploymentsRoute}
											params={{ projectKey: project.key }}
											iconBefore={<IconUpload label="" />}
											selectedOn={['software-deployments']}
											// Note: this test ID is used in a Pollinator check
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-deployments-link"
										>
											{formatMessage(messages.itemDeployments)}
										</MenuItem>
									) : (
										<MenuItem
											analytics={{
												itemId: 'deployments',
											}}
											href={`${projectUrl}/deployments`}
											iconBefore={<IconUpload label="" />}
											selectedOn={['software-deployments']}
											// Note: this test ID is used in a Pollinator check
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-deployments-link"
										>
											{formatMessage(messages.itemDeployments)}
										</MenuItem>
									))}
								{features.hasOpsgenie &&
									(determineProductCrossSellConfig() === 'enabled' ||
										licensedProducts[LICENSED_PRODUCTS.OPSGENIE]) && (
										<MenuItem
											analytics={{
												itemId: 'opsgenie',
												// @ts-expect-error - nextgen is not assignable to type 'Analytics'.
												nextgen: true,
												projectId: project.id,
											}}
											selectedOn={['opsgenie-schedule']}
											iconBefore={<IconOpsGenie />}
											href={`${projectUrl}/on-call-schedule`}
											// Note: this test ID is used in a Pollinator check
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.software-opsgenie-link"
										>
											{formatMessage(messages.itemOpsGenine)}
										</MenuItem>
									)}
								{((incidentExperimentTraits && isIncidentsPageEnabled(incidentExperimentTraits)) ||
									fg('corex_operandi_incidents_page_in_jira')) &&
									features.hasIncidents &&
									(incidentsRoute !== null ? (
										<MenuItem
											analytics={{ itemId: 'incidents' }}
											to={incidentsRoute}
											params={{ projectKey: project.key }}
											prefetch="hover"
											iconBefore={<IconIncidents label="" />}
											selectedOn={['software-incidents']}
											// Note: this test ID is used in a Pollinator check
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.incidents-link"
										>
											{formatMessage(messages.itemIncidents)}
											<LozengeContainer>
												<Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>
											</LozengeContainer>
											<FireUiAnalytics
												actionSubject="incidentsNavigationItem"
												attributes={{
													nextgen: true,
												}}
												action="viewed"
											/>
										</MenuItem>
									) : (
										<MenuItem
											analytics={{ itemId: 'incidents' }}
											href={`${projectUrl}/incidents`}
											iconBefore={<IconIncidents label="" />}
											selectedOn={['software-incidents']}
											// Note: this test ID is used in a Pollinator check
											data-testid="navigation-apps-sidebar-next-gen.ui.menu.incidents-link"
										>
											{formatMessage(messages.itemIncidents)}
											<LozengeContainer>
												<Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>
											</LozengeContainer>
											<FireUiAnalytics
												actionSubject="incidentsNavigationItem"
												attributes={{
													nextgen: true,
												}}
												action="viewed"
											/>
										</MenuItem>
									))}
							</ExpandableSection>
						)}
					</NavigationExpandableContentWrapper>
					<NavigationContentWrapper submenuActive={nestedMenuActive}>
						<ShortCutsAndAppsWrapper>
							<Section hasSeparator>
								{
									// in next-gen if there's no board, then the project is private
									features.hasBoard && (
										<Shortcuts
											canAddItem={features.hasShortcutsAdd}
											canAddLinks
											canAddRepositories
											hasPages={features.hasPages}
											isSimplifiedProject
											pagesLink={projectPagesData}
											projectId={project.id}
											projectKey={project.key}
										>
											<ForgeProjectSection
												nestedMenuActive={nestedMenuActive}
												overrides={{
													BackMenuItem: getBackMenuItem(true),
													ParentMenuItem: {
														onClick: nonExpandableSectionNestedOnClick,
													},
												}}
											/>
											<ConnectMenu
												items={connectItems}
												showIcons
												overrides={{
													BackMenuItem: getBackMenuItem(true),
													// @ts-expect-error - 'ParentMenuItem' does not exist in ConnectMenu props
													ParentMenuItem: {
														onClick: nonExpandableSectionNestedOnClick,
													},
												}}
											/>
										</Shortcuts>
									)
								}
								{shouldRenderNesting(features.hasSettings, stack, SECTION_ID.SETTINGS) && settings}
							</Section>
						</ShortCutsAndAppsWrapper>
						{(
							fg('archived_issues_in_nav4_sidebar') ? features?.hasArchivedIssues === true : true
						) ? (
							<Section hasSeparator>
								<MenuItem
									analytics={{ itemId: 'archives' }}
									selectedOn={
										fg('slv-3_migrate_archived_issues_routes')
											? ['software-archived-work-items']
											: ['archived-issues-directory']
									}
									iconBefore={<ArchiveIcon spacing="spacious" label="" />}
									href={
										fg('slv-3_migrate_archived_issues_routes')
											? `/jira/software/projects/${project.key}/archived-work-items`
											: `/jira/software/projects/${project.key}/issues/archive`
									}
									data-testid="navigation-apps-sidebar-next-gen.ui.menu.archives-link"
								>
									{formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.itemArchivesIssueTermRefresh
											: messages.itemArchives,
									)}
									<LozengeContainer>
										<Lozenge appearance="new">{formatMessage(messages.new)}</Lozenge>
									</LozengeContainer>
								</MenuItem>
							</Section>
						) : null}
					</NavigationContentWrapper>
				</NestableNavigationContent>
				<NavigationFooter>
					{/* Post Office Side-Nav Recommendations Experiment */}
					<JswSideNavRecommendation />
					{fg('jsw_simplify_navigation_m2_gate') ? <FooterSimplifyNavExperiment /> : <Footer />}
				</NavigationFooter>
			</SideNavigation>
			<RegisterProjectNavigationCommands
				timelineHref={timelineHref}
				backlogHref={backlogHref}
				boardHref={boardHref}
				reportsHref={reportsHref}
				features={{
					hasBacklog: board !== null && features.hasBacklog,
					hasBoard: board !== null && features.hasBoard,
					hasReports: board !== null && features.hasReports,
					hasRoadmap: board !== null && features.hasRoadmap,
				}}
			/>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LozengeContainer = styled.span({
	marginLeft: token('space.050'),
});
